import React, { useEffect, useState } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';
import { API, withCreds, inputCls, smallInputCls, selectCls, btnPrimary, btnSecondary, Field, PageHeader, Spinner, money, errMsg } from '../../components/admin/AdminUI';
import { Plus, Trash2, Check, Truck, Receipt, MapPin, Box, CheckCircle2, XCircle } from 'lucide-react';
import Select from '../../components/FlatSelect';

function Section({ icon: Icon, title, children, hint }) {
  return (
    <div className="admin-card rounded-xl p-5">
      <h2 className="text-ink font-bold flex items-center gap-2 mb-1"><Icon className="w-4 h-4 text-honey-700" /> {title}</h2>
      {hint && <p className="text-ink/55 text-xs mb-4">{hint}</p>}
      <div className={hint ? '' : 'mt-4'}>{children}</div>
    </div>
  );
}

function Integration({ ok, name, children }) {
  return (
    <div className="flex items-start gap-3 p-3 rounded-xl bg-ink/[0.04]">
      {ok ? <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" /> : <XCircle className="w-5 h-5 text-ink/45 flex-shrink-0" />}
      <div>
        <p className="text-ink text-sm font-medium">{name} <span className={`text-xs ${ok ? 'text-green-600' : 'text-ink/55'}`}>{ok ? 'connected' : 'not configured'}</span></p>
        <p className="text-ink/55 text-xs">{children}</p>
      </div>
    </div>
  );
}

const lbsOz = (oz) => (oz >= 16 ? `${+(oz / 16).toFixed(2)} lb` : `${oz} oz`);

export default function AdminSettings() {
  const [s, setS] = useState(null);
  const [status, setStatus] = useState({ shippo: null, uploads: null });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [testWeight, setTestWeight] = useState(24);

  useEffect(() => {
    axios.get(`${API}/admin/store-settings`, withCreds).then(r => setS(r.data));
    Promise.all([
      axios.get(`${API}/admin/shipping/status`, withCreds).catch(() => ({ data: {} })),
      axios.get(`${API}/admin/uploads/status`, withCreds).catch(() => ({ data: {} })),
    ]).then(([a, b]) => setStatus({ shippo: a.data, uploads: b.data }));
  }, []);

  if (!s) return <AdminLayout><Spinner /></AdminLayout>;

  const ship = s.shipping;
  const setShip = (k, v) => setS({ ...s, shipping: { ...ship, [k]: v } });
  const setTier = (i, k, v) => setShip('tiers', ship.tiers.map((t, j) => (j === i ? { ...t, [k]: v } : t)));
  const setFrom = (k, v) => setS({ ...s, ship_from: { ...s.ship_from, [k]: v } });
  const setParcel = (k, v) => setS({ ...s, default_parcel: { ...s.default_parcel, [k]: v } });

  const sortedTiers = [...ship.tiers].sort((a, b) => (a.max_weight_oz === '' || a.max_weight_oz == null ? Infinity : +a.max_weight_oz) - (b.max_weight_oz === '' || b.max_weight_oz == null ? Infinity : +b.max_weight_oz));
  const previewTier = sortedTiers.find(t => t.max_weight_oz === '' || t.max_weight_oz == null || testWeight <= +t.max_weight_oz) || sortedTiers[sortedTiers.length - 1];

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        ...s,
        tax_rate: parseFloat(s.tax_rate) || 0,
        low_stock_threshold: parseInt(s.low_stock_threshold) || 0,
        shipping: {
          ...ship,
          free_shipping_threshold: ship.free_shipping_threshold === '' || ship.free_shipping_threshold == null ? null : parseFloat(ship.free_shipping_threshold),
          default_weight_oz: parseFloat(ship.default_weight_oz) || 0,
        },
        default_parcel: Object.fromEntries(Object.entries(s.default_parcel).map(([k, v]) => [k, parseFloat(v) || 0])),
      };
      setS(await axios.put(`${API}/admin/store-settings`, body, withCreds).then(r => r.data));
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) { alert(errMsg(e, 'Save failed')); }
    finally { setSaving(false); }
  };

  return (
    <AdminLayout>
      <PageHeader title="Settings" subtitle="Tax, shipping, and fulfillment">
        <button onClick={save} disabled={saving} className={btnPrimary}>{saved ? <><Check className="w-4 h-4" /> Saved</> : saving ? 'Saving…' : 'Save settings'}</button>
      </PageHeader>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <Section icon={Truck} title="Shipping rates at checkout" hint="Customers pay based on the total weight of their cart. Each tier covers carts up to its weight; leave the last tier's weight blank to cover everything heavier.">
          <div className="space-y-2">
            {ship.tiers.map((t, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-ink/55 text-sm w-16">Up to</span>
                <input type="number" min="0" step="0.1" value={t.max_weight_oz ?? ''} onChange={e => setTier(i, 'max_weight_oz', e.target.value)} placeholder="any weight" className={`${smallInputCls} w-28`} />
                <span className="text-ink/55 text-sm">oz</span>
                <span className="text-ink/45 text-xs w-16">{t.max_weight_oz ? `(${lbsOz(+t.max_weight_oz)})` : ''}</span>
                <span className="text-ink/55 text-sm">→ $</span>
                <input type="number" min="0" step="0.01" value={t.price} onChange={e => setTier(i, 'price', e.target.value)} className={`${smallInputCls} w-24`} />
                <button onClick={() => setShip('tiers', ship.tiers.filter((_, j) => j !== i))} disabled={ship.tiers.length <= 1} className="text-ink/45 hover:text-red-600 disabled:opacity-20 ml-auto"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            <button onClick={() => setShip('tiers', [...ship.tiers, { max_weight_oz: '', price: 0 }])} className={`${btnSecondary} mt-2`}><Plus className="w-4 h-4" /> Add tier</button>
          </div>
          <div className="grid grid-cols-2 gap-4 mt-5">
            <Field label="Free shipping over ($)" hint="Leave blank for no free shipping">
              <input type="number" min="0" step="0.01" value={ship.free_shipping_threshold ?? ''} onChange={e => setShip('free_shipping_threshold', e.target.value)} className={inputCls} />
            </Field>
            <Field label="Default product weight (oz)" hint="Used for products with no weight set">
              <input type="number" min="0" step="0.1" value={ship.default_weight_oz} onChange={e => setShip('default_weight_oz', e.target.value)} className={inputCls} />
            </Field>
          </div>
          <div className="mt-4 p-3 rounded-xl bg-ink/[0.04] flex items-center gap-2 text-sm">
            <span className="text-ink/55">Preview: a</span>
            <input type="number" min="0" value={testWeight} onChange={e => setTestWeight(+e.target.value || 0)} className={`${smallInputCls} w-20`} />
            <span className="text-ink/55">oz cart ships for</span>
            <b className="text-honey-700">{previewTier ? money(previewTier.price) : '—'}</b>
          </div>
        </Section>

        <div className="space-y-6">
          <Section icon={Receipt} title="Tax">
            <Field label="Sales tax rate (%)" hint="Applied to the subtotal after discounts. Set 0 to turn off.">
              <input type="number" min="0" step="0.001" value={s.tax_rate} onChange={e => setS({ ...s, tax_rate: e.target.value })} className={`${inputCls} max-w-[160px]`} />
            </Field>
          </Section>

          <Section icon={Box} title="Inventory">
            <Field label="Low-stock alert at" hint="Products at or below this quantity show as low stock on the dashboard">
              <input type="number" min="0" value={s.low_stock_threshold} onChange={e => setS({ ...s, low_stock_threshold: e.target.value })} className={`${inputCls} max-w-[160px]`} />
            </Field>
          </Section>

          <Section icon={CheckCircle2} title="Connections">
            <div className="space-y-2">
              <Integration ok={status.shippo?.configured} name="Shippo (shipping labels)">
                {status.shippo?.configured
                  ? (status.shippo.test_mode ? 'Using a TEST key: labels are free and not valid for real shipping.' : 'Live key: labels you buy are charged to your Shippo account.')
                  : 'Add SHIPPO_API_KEY to the backend environment in Coolify.'}
              </Integration>
              <Integration ok={status.uploads?.configured} name="Cloudinary (image uploads)">
                {status.uploads?.configured ? 'Product and category images upload to Cloudinary.' : 'Add CLOUDINARY_CLOUD_NAME, CLOUDINARY_API_KEY and CLOUDINARY_API_SECRET to the backend environment.'}
              </Integration>
            </div>
          </Section>
        </div>

        <Section icon={MapPin} title="Ship-from address" hint="Printed as the return address on labels and packing slips. Required before buying labels.">
          <div className="grid grid-cols-2 gap-3">
            {[['name', 'Contact name *', 2], ['company', 'Business name', 2], ['street1', 'Street *', 2], ['street2', 'Suite / unit', 2],
              ['city', 'City *', 1], ['state', 'State *', 1], ['zip', 'ZIP *', 1], ['country', 'Country *', 1], ['phone', 'Phone', 1], ['email', 'Email', 1]].map(([k, l, span]) => (
              <Field key={k} label={l} className={span === 2 ? 'col-span-2' : ''}>
                <input value={s.ship_from[k] || ''} onChange={e => setFrom(k, e.target.value)} className={inputCls} />
              </Field>
            ))}
          </div>
          {status.shippo?.ship_from_missing?.length > 0 && <p className="text-amber-600/80 text-xs mt-3">Still needed: {status.shippo.ship_from_missing.join(', ')}</p>}
        </Section>

        <Section icon={Box} title="Labels" hint="Defaults you can change per order when buying a label.">
          <div className="grid grid-cols-3 gap-3 mb-4">
            {[['length', 'Box length (in)'], ['width', 'Box width (in)'], ['height', 'Box height (in)']].map(([k, l]) => (
              <Field key={k} label={l}><input type="number" min="0" step="0.1" value={s.default_parcel[k]} onChange={e => setParcel(k, e.target.value)} className={inputCls} /></Field>
            ))}
          </div>
          <Field label="Label format">
            <Select value={s.label_file_type} onChange={e => setS({ ...s, label_file_type: e.target.value })} className={`${selectCls} w-full`}>
              <option value="PDF_4x6">4×6 PDF (thermal label printers)</option>
              <option value="PDF">8.5×11 PDF (regular printer)</option>
              <option value="PNG">PNG image</option>
              <option value="ZPLII">ZPL (Zebra printers)</option>
            </Select>
          </Field>
        </Section>
      </div>
    </AdminLayout>
  );
}
